import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/site/Layout";
import { toast } from "sonner";
import { Star, X, ImageIcon, Upload } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function uploadOne(file, onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  const token = localStorage.getItem("srl_token");
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BACKEND}/api/admin/upload/image`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
      else reject(new Error(xhr.responseText));
    };
    xhr.onerror = () => reject(new Error("upload failed"));
    xhr.send(fd);
  });
}

export default function AdminUpload() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [showNewModel, setShowNewModel] = useState(false);
  const [newModel, setNewModel] = useState({ name: "", bio: "", profile_photo: "", tags: "" });
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [galleryId, setGalleryId] = useState(null);
  const [images, setImages] = useState([]); // {image_id,url,hires_url,credit_cost,progress,uploading}
  const [selected, setSelected] = useState(new Set());
  const [cover, setCover] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const fromRequestId = params.get("request");
  const editGalleryId = params.get("edit");

  useEffect(() => { api.get("/models").then(r => setModels(r.data)); }, []);

  // Pre-fill from greenlit request
  useEffect(() => {
    if (!fromRequestId) return;
    api.get(`/admin/greenlit/${fromRequestId}`).then(({ data }) => {
      if (data.model_id) setModelId(data.model_id);
      if (data.teaser_gallery_title) setTitle(data.teaser_gallery_title.replace(/^Teaser:\s*/i, "") + " · Full Edition");
      toast.success(`Fulfilling greenlit request from ${data.model_name}`);
    }).catch(() => {});
  }, [fromRequestId]);

  // Load existing gallery for edit
  useEffect(() => {
    if (!editGalleryId) return;
    api.get(`/admin/galleries/${editGalleryId}`).then(({ data }) => {
      setGalleryId(data.gallery_id);
      setModelId(data.model_id);
      setTitle(data.title);
      setDescription(data.description || "");
      setCover(data.cover_image || "");
      setImages((data.images || []).map(im => ({ ...im, progress: 1, uploading: false })));
      toast.success("Editing published gallery");
    }).catch(() => toast.error("Could not load gallery"));
  }, [editGalleryId]);

  const ensureDraft = async () => {
    if (galleryId) return galleryId;
    if (editGalleryId) return editGalleryId;
    if (!modelId) { toast.error("Pick a model first"); return null; }
    if (!title) { toast.error("Add a gallery title"); return null; }
    const { data } = await api.post("/admin/galleries/draft", {
      model_id: modelId, title, description, from_request_image_id: fromRequestId,
    });
    setGalleryId(data.gallery_id);
    return data.gallery_id;
  };

  const onDrop = useCallback(async (files) => {
    const gid = await ensureDraft();
    if (!gid) return;
    if (!title) setTitle(files[0]?.name.replace(/\.[^.]+$/, "").slice(0, 60) || "Untitled");
    // Add placeholders
    const start = images.length;
    const placeholders = files.map((f, i) => ({
      image_id: `tmp_${start + i}`, url: "", hires_url: "", credit_cost: 1,
      original_filename: f.name, progress: 0, uploading: true,
    }));
    setImages(prev => [...prev, ...placeholders]);
    // Parallel with concurrency 5
    const queue = files.map((f, i) => ({ f, idx: start + i }));
    const workers = Array.from({ length: 5 }, async () => {
      while (queue.length) {
        const { f, idx } = queue.shift();
        try {
          const r = await uploadOne(f, (p) => {
            setImages(prev => prev.map((x, j) => j === idx ? { ...x, progress: p } : x));
          });
          setImages(prev => prev.map((x, j) => j === idx ? { ...r, progress: 1, uploading: false } : x));
        } catch {
          setImages(prev => prev.map((x, j) => j === idx ? { ...x, uploading: false, error: true } : x));
        }
      }
    });
    await Promise.all(workers);
    toast.success(`${files.length} images uploaded`);
  }, [galleryId, modelId, title, description, fromRequestId, images.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { "image/*": [] }, noClick: false, noKeyboard: true,
  });

  const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkHero = (cost) => {
    setImages(prev => prev.map(x => selected.has(x.image_id) ? { ...x, credit_cost: cost } : x));
    setSelected(new Set());
    toast.success(`Marked ${selected.size} as ${cost === 1 ? "standard" : `hero ${cost}¢`}`);
  };
  const removeImg = (id) => setImages(prev => prev.filter(x => x.image_id !== id));

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e, i) => { e.preventDefault(); if (dragIdx === null || dragIdx === i) return; };
  const onDropReorder = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    setImages(prev => {
      const arr = [...prev]; const [it] = arr.splice(dragIdx, 1); arr.splice(i, 0, it); return arr;
    });
    setDragIdx(null);
  };

  const saveDraft = async () => {
    if (!galleryId) return;
    const payload = {
      title, description,
      cover_image: cover || images[0]?.url || "",
      images: images.filter(x => !x.uploading && !x.error).map(x => ({
        image_id: x.image_id, url: x.url, hires_url: x.hires_url, credit_cost: x.credit_cost,
        original_filename: x.original_filename,
      })),
    };
    await api.patch(`/admin/galleries/${galleryId}`, payload);
  };

  const publish = async () => {
    if (!galleryId) return;
    setPublishing(true);
    try {
      await saveDraft();
      await api.post(`/admin/galleries/${galleryId}/publish`);
      toast.success("Gallery published live!");
      nav("/admin");
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    finally { setPublishing(false); }
  };

  const createInlineModel = async () => {
    try {
      const { data } = await api.post("/admin/models", {
        ...newModel, tags: newModel.tags.split(",").map(s => s.trim()).filter(Boolean),
      });
      setModels(m => [...m, data]);
      setModelId(data.model_id);
      setShowNewModel(false);
      setNewModel({ name: "", bio: "", profile_photo: "", tags: "" });
      toast.success("Model created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filteredModels = models.filter(m => m.name.toLowerCase().includes(modelSearch.toLowerCase()));
  const doneCount = images.filter(x => !x.uploading && !x.error).length;
  const totalProgress = images.length ? images.reduce((s, x) => s + (x.progress || 0), 0) / images.length : 0;

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 pb-24">
        <div className="overline mb-3">Admin · Upload</div>
        <h1 className="font-serif-editorial text-5xl tracking-tighter mb-10">New gallery.</h1>

        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <div className="overline mb-2">Model</div>
            <input value={modelSearch} onChange={e => setModelSearch(e.target.value)}
              placeholder="Search models…" data-testid="upload-model-search"
              className="w-full bg-transparent border border-white/20 px-3 py-2 outline-none focus:border-white text-sm mb-2"/>
            <select value={modelId} onChange={e => setModelId(e.target.value)}
              data-testid="upload-model-select"
              className="w-full bg-transparent border border-white/20 px-3 py-2 outline-none focus:border-white text-sm">
              <option value="" className="bg-black">— select —</option>
              {filteredModels.map(m => <option key={m.model_id} value={m.model_id} className="bg-black">{m.name}</option>)}
            </select>
            <button onClick={() => setShowNewModel(v => !v)} className="text-xs font-mono-ui text-neutral-400 hover:text-white mt-2 uppercase tracking-widest" data-testid="upload-new-model-toggle">+ New model inline</button>
            {showNewModel && (
              <div className="mt-3 space-y-2 border border-white/10 p-3">
                <input placeholder="Name" value={newModel.name} onChange={e=>setNewModel({...newModel, name:e.target.value})} className="w-full bg-transparent border border-white/20 px-2 py-1 text-sm" data-testid="inline-model-name"/>
                <input placeholder="Profile photo URL" value={newModel.profile_photo} onChange={e=>setNewModel({...newModel, profile_photo:e.target.value})} className="w-full bg-transparent border border-white/20 px-2 py-1 text-sm"/>
                <input placeholder="Tags (comma)" value={newModel.tags} onChange={e=>setNewModel({...newModel, tags:e.target.value})} className="w-full bg-transparent border border-white/20 px-2 py-1 text-sm"/>
                <button onClick={createInlineModel} className="btn-white !py-1 !px-3 !text-[10px]" data-testid="inline-model-create">Create</button>
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <div className="overline mb-2">Gallery title</div>
            <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Auto-filled from folder name"
              data-testid="upload-title"
              className="w-full bg-transparent border border-white/20 px-3 py-2 outline-none focus:border-white text-lg font-serif-editorial mb-3"/>
            <input value={description} onChange={e=>setDescription(e.target.value)} placeholder="Description (optional)"
              data-testid="upload-description"
              className="w-full bg-transparent border border-white/20 px-3 py-2 outline-none focus:border-white text-sm"/>
          </div>
        </div>

        <div {...getRootProps()}
          className={`border-2 border-dashed p-16 text-center cursor-pointer transition-colors ${isDragActive ? "border-white bg-white/5" : "border-white/20 hover:border-white/40"}`}
          data-testid="upload-dropzone">
          <input {...getInputProps()} data-testid="upload-input"/>
          <Upload size={32} className="mx-auto mb-4 text-neutral-400"/>
          <div className="font-serif-editorial text-2xl mb-2">Drop images here</div>
          <div className="text-xs font-mono-ui text-neutral-500 uppercase tracking-widest">
            {isDragActive ? "Release to upload" : "Or click · JPG/PNG · batches of 50+ supported"}
          </div>
        </div>

        {images.length > 0 && (
          <>
            <div className="mt-6 flex items-center justify-between gap-4">
              <div className="text-xs font-mono-ui text-neutral-400 uppercase tracking-widest">
                {doneCount}/{images.length} uploaded
              </div>
              <div className="flex-1 progress-track max-w-md"><div className="progress-fill" style={{width:`${totalProgress*100}%`}}/></div>
              {selected.size > 0 && (
                <div className="flex gap-2">
                  <button onClick={()=>bulkHero(1)} className="btn-ghost !py-1 !px-3 !text-[10px]" data-testid="bulk-standard">Standard 1¢</button>
                  <button onClick={()=>bulkHero(2)} className="btn-ghost !py-1 !px-3 !text-[10px]" data-testid="bulk-hero-2">Hero 2¢</button>
                  <button onClick={()=>bulkHero(3)} className="btn-white !py-1 !px-3 !text-[10px]" data-testid="bulk-hero-3">Hero 3¢</button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-6" data-testid="upload-grid">
              {images.map((img, i) => (
                <div key={img.image_id}
                  draggable={!img.uploading}
                  onDragStart={() => onDragStart(i)}
                  onDragOver={(e) => onDragOver(e, i)}
                  onDrop={() => onDropReorder(i)}
                  className={`relative aspect-[3/4] border ${selected.has(img.image_id) ? "border-white" : "border-white/10"} cursor-move overflow-hidden group`}
                  data-testid={`upload-tile-${i}`}>
                  {img.url ? (
                    <img src={`${BACKEND}${img.url}`} alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-neutral-900">
                      <ImageIcon size={20} className="text-neutral-600"/>
                    </div>
                  )}
                  {img.uploading && (
                    <div className="absolute inset-x-0 bottom-0 progress-track"><div className="progress-fill" style={{width:`${(img.progress||0)*100}%`}}/></div>
                  )}
                  {img.error && <div className="absolute inset-0 bg-red-900/60 flex items-center justify-center text-xs">FAIL</div>}
                  {!img.uploading && !img.error && (
                    <>
                      <button onClick={() => toggleSel(img.image_id)}
                        className={`absolute inset-0 ${selected.has(img.image_id) ? "bg-white/20" : "bg-transparent hover:bg-black/30"}`}
                        data-testid={`select-${i}`}/>
                      <div className="absolute top-2 left-2 flex gap-1 pointer-events-none">
                        {img.credit_cost > 1 && <div className="ribbon !py-0.5 !px-1.5 !text-[8px] bg-white text-black">H{img.credit_cost}</div>}
                      </div>
                      <div className="absolute top-2 right-2 flex gap-1">
                        <button onClick={(e)=>{e.stopPropagation(); setCover(img.url);}}
                          className={`w-6 h-6 flex items-center justify-center ${cover===img.url ? "bg-white text-black" : "bg-black/60 text-white/70 hover:text-white"}`}
                          data-testid={`cover-${i}`} title="Set cover">
                          <Star size={12} fill={cover===img.url?"currentColor":"none"}/>
                        </button>
                        <button onClick={(e)=>{e.stopPropagation(); removeImg(img.image_id);}}
                          className="w-6 h-6 flex items-center justify-center bg-black/60 text-white/70 hover:text-white"
                          data-testid={`remove-${i}`}><X size={12}/></button>
                      </div>
                      <div className="absolute bottom-1 left-2 text-[10px] font-mono-ui text-white/70 pointer-events-none">#{i+1}</div>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center justify-between border-t border-white/10 pt-6">
              <div className="text-xs font-mono-ui text-neutral-500 uppercase tracking-widest">
                {galleryId ? `Draft · ${galleryId.slice(0,12)}` : "Not saved"} {fromRequestId && "· fulfilling greenlit request"}
              </div>
              <div className="flex gap-2">
                <button onClick={saveDraft} className="btn-ghost" data-testid="save-draft">Save draft</button>
                <button onClick={publish} disabled={publishing || doneCount === 0} className="btn-white" data-testid="publish-gallery">
                  {publishing ? "..." : "Publish live"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
